import { atom, selector, selectorFamily, useRecoilCallback } from 'recoil';
import { effectConsoleLogAtomUpdates } from '../helpers/debug';
import type {
  CaseOrderHistory,
  PushBackSkipOffer,
  SavingsAccountBalanceResponse,
  SavingsAccountResponse,
  WinePlanTypes,
} from 'tsconfig/types';
import { initCommerceApiWithGlobals } from 'common-api';
import { logger } from 'common-ui';

// Storing the upcomming case details in wine plan screen
export const winePlanUpcomingCaseAtom = atom<WinePlanTypes>({
  key: 'winePlanUpcomingCaseAtom', // It saves the upcoming case details data
  default: undefined,
  effects: [effectConsoleLogAtomUpdates],
});

//Selectors
//----------------------------------------------------------------------

export const selectUpcomingCaseData = selector({
  key: 'selectUpcomingCaseData',
  get: ({ get }) => {
    try {
      const upcomingCaseData = get(winePlanUpcomingCaseAtom);
      if (upcomingCaseData) {
        return upcomingCaseData;
      }
      return undefined;
    } catch (error) {
      logger.error('Error in selectUpcomingCaseData:', error);
      return undefined;
    }
  },
});

export const caseOrderHistoryAtom = atom<CaseOrderHistory>({
  key: 'caseOrderHistoryAtom', // It saves the case order history data
  default: undefined,
  effects: [effectConsoleLogAtomUpdates],
});

export const pushBackSkipOfferAtom = atom<PushBackSkipOffer>({
  key: 'pushBackSkipOfferAtom',
  default: undefined,
  effects: [effectConsoleLogAtomUpdates],
});

export const selectCaseOrderHistoryData = selector({
  key: 'selectCaseOrderHistoryData',
  get: ({ get }) => {
    try {
      const caseOrderHistoryData = get(caseOrderHistoryAtom);
      if (caseOrderHistoryData) {
        return caseOrderHistoryData;
      }
      return undefined;
    } catch (error) {
      logger.error('Error in selectCaseOrderHistoryData:', error);
      throw new Error('Something went wrong');
    }
  },
});

export const fetchWinePlanSubscriptionDetails = selectorFamily({
  key: 'winePlanSubscriptionDetailsSelector',
  get: (subscriptionId: string) => async () => {
    try {
      const commerceApi = initCommerceApiWithGlobals();
      return await commerceApi.WinePlan.getSupportedFrequencies(subscriptionId);
    } catch (error) {
      logger.log('API ERROR', error);
    }
  },
});

export const userDeliveryAddressAtom = atom<any>({
  key: 'winePlanUserDeliveryAddressAtom',
  default: {
    isLoading: false,
    data: [],
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const userSelectedAddressAtom = atom({
  key: 'winePlanUserSelectedAddressAtom',
  default: {
    isLoading: false,
    hasError: false,
    data: null,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const wineplanPincodeSearchAtom = atom<any>({
  key: 'wineplanPincodeSearchAtom',
  default: {
    isLoading: false,
    hasError: false,
    data: null,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const wineplanUpdateDeliveryAddressAtom = atom({
  key: 'wineplanUpdateDeliveryAddressAtom',
  default: {
    isLoading: false,
    hasError: false,
    isAddressSaved: false,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const wineplanUseDeliveryAddressAtom = atom({
  key: 'wineplanUseDeliveryAddressAtom',
  default: {
    isLoading: false,
    hasError: false,
    isAddressUpdated: false,
    selectedAddressId: '',
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const wineplanShopPincodeSearchAtom = atom<any>({
  key: 'wineplanShopPincodeSearchAtom',
  default: {
    isLoading: false,
    hasError: false,
    data: null,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const wineplanUseStoreDeliveryAddressAtom = atom({
  key: 'wineplanUseStoreDeliveryAddressAtom',
  default: {
    isLoading: false,
    hasError: false,
    isAddressUpdated: false,
    selectedAddressId: '',
  },
  effects: [effectConsoleLogAtomUpdates],
});

const checkWinePlanEligibility = (savingsAccount: SavingsAccountResponse) => {
  if (savingsAccount.response.numSavingsAccounts > 0) {
    const eligibleAccounts = savingsAccount.response.accounts.filter(
      account => account.accountType === 'WINEREWARD' && account.status === 'OPEN',
    );
    if (eligibleAccounts.length > 0) {
      return {
        isEligibile: true,
        account: eligibleAccounts[0],
      };
    }
  }
  return {
    isEligibile: false,
  };
};

export const fetchWinePlanDefaultPaymentDetails = selectorFamily({
  key: 'winePlanDefaultPaymentDetailsSelector',
  get: (subscriptionId: string) => async () => {
    try {
      const commerceApi = initCommerceApiWithGlobals();
      return await commerceApi.WinePlan.getPaymentMethods(subscriptionId);
    } catch (error) {
      logger.log('API ERROR', error);
    }
  },
});

export const fetchWinePlanAccountDetails = selectorFamily({
  key: 'winePlanAccountDetailsSelector',
  get: () => async () => {
    try {
      const commerceApi = initCommerceApiWithGlobals();
      const response: SavingsAccountResponse = await commerceApi.WinePlan.getSavingsAccount();
      const eligibilityResponse = checkWinePlanEligibility(response);
      if (eligibilityResponse.isEligibile) {
        const balanceResponse: SavingsAccountBalanceResponse = await commerceApi.WinePlan.getBalanceForAccount(
          eligibilityResponse.account?.accountId,
        );
        return { isEligible: true, balance: balanceResponse.response.balance };
      }
      return { isEligible: false };
    } catch (error) {
      logger.log('API ERROR', error);
    }
  },
});

export const wineplanAvailableCaseOptionsAtom = atom<any>({
  key: 'wineplanAvailableCaseOptionsAtom',
  default: {
    isLoading: false,
    hasError: false,
    caseOptions: [],
    caseDetails: null,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const wineplanSwapSubscriptionAtom = atom({
  key: 'wineplanSwapSubscriptionAtom',
  default: {
    isLoading: false,
    hasError: false,
    isSubscriptionSwapped: false,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export interface WineCase {
  id: string;
  name: string;
  quantity: number;
  image: string;
}

/**Atom for flex */
export const flexState = atom<WineCase[]>({
  key: 'flexState',
  default: [],
});

export const flexMobileFilterState = atom<boolean>({
  key: 'flexMobileFilterState',
  default: false,
});

export const flexCaseDetailsState = atom<{ casePrice: number; savePrice: number; totalQuantity: number }>({
  key: 'flexCaseDetailsState',
  default: {
    casePrice: 0,
    savePrice: 0,
    totalQuantity: 0,
  },
});

export const flexAPIState = atom<boolean>({
  key: 'flexAPIState',
  default: false,
});

export const dirtyFlexCartState = atom<boolean>({
  key: 'dirtyFlexCartState',
  default: false,
});

export interface AlgoliaHit {
  objectID: string;
  itemCode: string;
  productName: string;
  vintage?: string;
  productType?: string;
  skus?: Array<{ salePrice: number; listPrice?: number }>;
  avgRating?: number;
  numberOfReviews?: number;
  [key: string]: any; // Allow additional properties from Algolia
}

export const flexSearchResultsAtom = atom<any[]>({
  key: 'flexSearchResultsAtom',
  default: [],
});

// Memoized selector that only updates when search results actually change
export const flexSearchResultsMemoizedSelector = selector<any[]>({
  key: 'flexSearchResultsMemoizedSelector',
  get: ({ get }) => get(flexSearchResultsAtom),
  cachePolicy_UNSTABLE: {
    // Cache results to prevent unnecessary recalculations
    eviction: 'most-recent',
  },
});

export const flexCartTotalItemsSelector = selector({
  key: 'flexCartTotalItemsSelector',
  get: ({ get }) => {
    const cart = get(flexCaseDetailsState);
    return cart.totalQuantity;
  },
});

export const flexCartCountByProductSelector = selectorFamily({
  key: 'flexCartCountByProductSelector',
  get:
    (productId: string) =>
    ({ get }) => {
      const cart = get(flexState);
      const item = cart.find(wineCase => wineCase.id === productId);
      return item ? item.quantity : 0;
    },
});

export const flexCartTotalPriceSelector = selector({
  key: 'flexCartTotalPriceSelector',
  get: ({ get }) => {
    const cart = get(flexCaseDetailsState);
    return cart.casePrice;
  },
});

export const flexCartTotalDiscountSelector = selector({
  key: 'flexCartTotalDiscountSelector',
  get: ({ get }) => {
    const cart = get(flexCaseDetailsState);
    return cart.savePrice;
  },
});

// Selector to check if cart is empty
export const isFlexCartEmptySelector = selector({
  key: 'isFlexCartEmptySelector',
  get: ({ get }) => {
    const cart = get(flexState);
    return cart.length === 0;
  },
});

export const useFlexCartOperations = () => {
  const addWineCase = useRecoilCallback(({ set, snapshot }) => async (wineCase: WineCase) => {
    const currentCart = await snapshot.getPromise(flexState);
    const existingCase = currentCart.find(item => item.id === wineCase.id);
    if (existingCase) {
      const updatedCart = currentCart.map(item =>
        item.id === wineCase.id ? { ...item, quantity: item.quantity + (wineCase.quantity || 1) } : item,
      );
      set(flexState, updatedCart);
    } else {
      const newWineCase = {
        ...wineCase,
        quantity: wineCase.quantity || 1,
      };
      set(flexState, [...currentCart, newWineCase]);
    }
    set(dirtyFlexCartState, true);
  });

  const updateWineCaseQuantity = useRecoilCallback(({ set, snapshot }) => async (id: string, quantity: number) => {
    const currentCart = await snapshot.getPromise(flexState);

    if (quantity <= 0) {
      const updatedCart = currentCart.filter(item => item.id !== id);
      set(flexState, updatedCart);
    } else {
      const updatedCart = currentCart.map(item => (item.id === id ? { ...item, quantity } : item));
      set(flexState, updatedCart);
    }
    set(dirtyFlexCartState, true);
  });

  const removeWineCase = useRecoilCallback(({ set, snapshot }) => async (id: string) => {
    const currentCart = await snapshot.getPromise(flexState);
    const updatedCart = currentCart.filter(item => item.id !== id);
    set(flexState, updatedCart);
    set(dirtyFlexCartState, true);
  });

  const updateWineCase = useRecoilCallback(({ set, snapshot }) => async (id, updates: any) => {
    const currentCart = await snapshot.getPromise(flexState);
    const updatedCart = currentCart.map(item => (item.id === id ? { ...item, ...updates } : item));
    set(flexState, updatedCart);
    set(dirtyFlexCartState, true);
  });

  const clearCart = useRecoilCallback(({ set }) => () => {
    set(flexState, []);
    set(dirtyFlexCartState, true);
  });

  return {
    addWineCase,
    updateWineCaseQuantity,
    removeWineCase,
    updateWineCase,
    clearCart,
  };
};

export const wineplanFlexSubscriptionAtom = atom({
  key: 'wineplanFlexSubscriptionAtom',
  default: {
    isLoading: false,
    hasError: false,
    isWinePlanModified: false,
  },
  effects: [effectConsoleLogAtomUpdates],
});

export const isWinePlanFlexed = atom({
  key: 'isWinePlanFlexed',
  default: false,
  effects: [effectConsoleLogAtomUpdates],
});

export const isCancelSaveOfferApplied = atom({
  key: 'isCancelSaveOfferApplied',
  default: false,
  effects: [effectConsoleLogAtomUpdates],
});

export const showFloatingCartFlag = atom({
  key: 'showFloatingFlag',
  default: false,
});

export const accordionValue = atom<string[]>({
  key: 'accordionValue',
  default: [],
});

export const flexModalState = atom<boolean>({
  key: 'flexModalState',
  default: false,
});
